import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Customer } from '../types';

export class CustomerService {
  // Add new customer
  static async addCustomer(salonId: string, customerData: Omit<Customer, 'id' | 'salonId' | 'createdAt' | 'updatedAt' | 'totalSpent' | 'visitCount'>): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, 'customers'), {
        ...customerData,
        salonId,
        totalSpent: 0,
        visitCount: 0,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });

      return docRef.id;
    } catch (error) {
      console.error('Error adding customer:', error);
      throw new Error('Failed to add customer');
    }
  }

  // Update customer
  static async updateCustomer(customerId: string, updates: Partial<Omit<Customer, 'id' | 'createdAt'>>): Promise<void> {
    try {
      const customerRef = doc(db, 'customers', customerId);
      await updateDoc(customerRef, {
        ...updates,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error updating customer:', error);
      throw new Error('Failed to update customer');
    }
  }

  // Get all customers for a salon
  static async getCustomers(salonId: string): Promise<Customer[]> {
    try {
      const q = query(
        collection(db, 'customers'),
        where('salonId', '==', salonId)
      );

      const querySnapshot = await getDocs(q);
      const customers: Customer[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        customers.push({
          id: doc.id,
          ...data,
          lastVisit: data.lastVisit?.toDate(),
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate(),
        } as Customer);
      });

      // Sort by last visit date (most recent first)
      return customers.sort((a, b) => {
        if (!a.lastVisit && !b.lastVisit) return 0;
        if (!a.lastVisit) return 1;
        if (!b.lastVisit) return -1;
        return b.lastVisit.getTime() - a.lastVisit.getTime();
      });
    } catch (error) {
      console.error('Error getting customers:', error);
      throw new Error('Failed to get customers');
    }
  }

  // Get customer by ID
  static async getCustomer(customerId: string): Promise<Customer | null> {
    try {
      const docRef = doc(db, 'customers', customerId);
      const docSnap = await getDocs(query(collection(db, 'customers'), where('__name__', '==', docRef)));

      if (!docSnap.empty) {
        const data = docSnap.docs[0].data();
        return {
          id: docSnap.docs[0].id,
          ...data,
          lastVisit: data.lastVisit?.toDate(),
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate(),
        } as Customer;
      }

      return null;
    } catch (error) {
      console.error('Error getting customer:', error);
      throw new Error('Failed to get customer');
    }
  }

  // Delete customer
  static async deleteCustomer(customerId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, 'customers', customerId));
    } catch (error) {
      console.error('Error deleting customer:', error);
      throw new Error('Failed to delete customer');
    }
  }
}
